import { Suspense, useEffect, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, Moon, Sun, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { BrandSwitcher, PauseExternalActionsBanner } from '@cedarreachai/ui';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { ErrorBoundary } from '@/app/errorBoundary';
import { useWorkspaceScope } from '@/app/WorkspaceContext';
import { usePauseState, useSyncPauseFromWorkspace } from '@/features/portfolio/pauseState';
import { useSession, useWorkspaceDetail } from '@/features/settings/useWorkspace';
import { listBrands } from '@/lib/api/endpoints';
import { queryKeys } from '@/lib/queryKeys';
import { changeLocale, enabledLocales, resolveLocale, type SupportedLocale } from '@/lib/i18n';
import { resolveChineseUi } from '@/lib/env';
import { useUiStore } from '@/stores/uiStore';
import { cn } from '@/lib/cn';
import { SignOutButton } from '@/app/auth/SignOutButton';
import { SideNav } from './SideNav';
import { MAIN_CONTENT_ID, SkipToContent } from './SkipToContent';

/**
 * Each language named in itself (an autonym), the convention for a language
 * picker: someone who cannot read the current interface must still be able to
 * find their own language in the list.
 */
const NAV_TOGGLE_ID = 'app-nav-toggle';

const LOCALE_LABELS: Readonly<Record<SupportedLocale, string>> = {
  'en-CA': 'English (Canada)',
  'zh-CN': '简体中文',
};

/**
 * The application frame.
 *
 * Responsive per design doc §22.5: the navigation is a persistent rail from
 * `lg` up and a disclosure panel below it, so portfolio status, approvals and
 * the pause control stay reachable on a phone.
 *
 * The shell reads `GET /workspaces/{id}` itself, so the kill-switch banner
 * reflects a server-side pause on whichever screen is opened first — not only
 * after Portfolio or Settings happened to load (§10.5, §22.3).
 */
export function AppShell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { workspaceId } = useWorkspaceScope();
  const { brandId } = useParams();
  const [navOpen, setNavOpen] = useState(false);
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const pause = usePauseState(workspaceId);
  const workspace = useWorkspaceDetail(workspaceId);
  useSyncPauseFromWorkspace(workspaceId, workspace.data);
  // The server's `chinese_ui` flag wins; the Vite variable is only a fallback.
  const session = useSession();
  const locales = enabledLocales(resolveChineseUi(session.data?.features?.chinese_ui));

  // Escape closes the narrow-screen navigation and hands focus back to its toggle.
  useEffect(() => {
    if (!navOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setNavOpen(false);
      document.getElementById(NAV_TOGGLE_ID)?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [navOpen]);

  const workspaceName = workspace.data?.name ?? null;

  const brands = useQuery({
    queryKey: queryKeys.brands(workspaceId),
    queryFn: ({ signal }) => listBrands(workspaceId, signal),
  });

  const options = (brands.data?.items ?? []).map((b) => ({ brandId: b.id, name: b.name }));

  return (
    <div data-theme={theme} className="flex min-h-dvh flex-col bg-background text-foreground">
      <SkipToContent />

      {/*
        Never dismissible while active. The component renders nothing when the
        workspace is not known to be paused, so it cannot cry wolf.
      */}
      <PauseExternalActionsBanner
        active={pause.known && pause.paused}
        workspaceName={
          workspaceName
            ? t('pause.bannerWorkspaceNamed', { name: workspaceName, id: workspaceId })
            : t('pause.bannerWorkspace', { id: workspaceId })
        }
        pausedAt={pause.observedAt}
        reason={pause.note}
      />

      <header className="flex flex-wrap items-center gap-x-2 gap-y-2 border-b border-border px-3 py-2">
        <Button
          id={NAV_TOGGLE_ID}
          variant="ghost"
          size="icon"
          className="lg:hidden"
          aria-expanded={navOpen}
          aria-controls="app-nav"
          aria-label={navOpen ? t('app.closeNavigation') : t('app.openNavigation')}
          onClick={() => setNavOpen((open) => !open)}
        >
          {navOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </Button>

        <span className="text-sm font-semibold tracking-tight">{t('app.name')}</span>

        <div
          className="flex min-w-0 items-center gap-2 text-sm"
          data-testid="shell-workspace"
          aria-label={t('app.workspaceLabel')}
          role="group"
        >
          {workspace.data ? (
            <>
              <span className="max-w-[12rem] truncate font-medium" title={workspace.data.name}>
                {workspace.data.name}
              </span>
              <span
                className="rounded-sm border border-border px-1.5 py-0.5 text-xs text-muted-foreground"
                data-testid="shell-automation-level"
              >
                {t('app.automationBadge', {
                  level: t(`settings.automation.level.${workspace.data.automation_level}`),
                })}
              </span>
            </>
          ) : (
            <span className="text-muted-foreground">
              {workspace.isError
                ? t('pause.bannerWorkspace', { id: workspaceId })
                : t('app.workspaceLoading')}
            </span>
          )}
        </div>

        <div className="w-full sm:w-auto">
          <BrandSwitcher
            brands={options}
            currentBrandId={brandId ?? null}
            onSelect={(id) => navigate(`/w/${workspaceId}/brands/${id}/facts`)}
            className="w-full sm:w-56"
          />
        </div>

        <div className="ml-auto flex items-center gap-2">
          {locales.length > 1 ? (
            <>
              <label className="sr-only" htmlFor="locale-select">
                {t('app.language')}
              </label>
              <Select
                id="locale-select"
                value={resolveLocale(i18n.language)}
                onChange={(event) => {
                  // The instance in context, not the module singleton: a test (or a
                  // second root) must be able to switch its own language.
                  void changeLocale(event.target.value as SupportedLocale, i18n);
                }}
              >
                {locales.map((locale) => (
                  <option key={locale} value={locale} lang={locale}>
                    {LOCALE_LABELS[locale]}
                  </option>
                ))}
              </Select>
            </>
          ) : null}

          <Button
            variant="ghost"
            size="icon"
            aria-label={t('app.theme.label')}
            aria-pressed={theme === 'dark'}
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          >
            {theme === 'dark' ? <Moon aria-hidden="true" /> : <Sun aria-hidden="true" />}
            <span className="sr-only">
              {theme === 'dark' ? t('app.theme.dark') : t('app.theme.light')}
            </span>
          </Button>

          <SignOutButton />
        </div>
      </header>

      <div className="flex flex-1 flex-col lg:flex-row">
        <div
          id="app-nav"
          className={cn(
            'shrink-0 border-b border-border lg:block lg:w-60 lg:border-b-0 lg:border-r',
            navOpen ? 'block' : 'hidden',
          )}
        >
          <SideNav onNavigate={() => setNavOpen(false)} />
        </div>

        <main id={MAIN_CONTENT_ID} tabIndex={-1} className="min-w-0 flex-1 p-4 lg:p-6">
          <h2 className="sr-only">{t('app.mainContent')}</h2>
          <ErrorBoundary>
            <Suspense
              fallback={
                <p role="status" aria-live="polite" className="text-sm text-muted-foreground">
                  {t('common.loading')}
                </p>
              }
            >
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
